<template>
  <div class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9997]">
    <Transition name="slide">
      <div 
        v-if="show"
        class="bg-red-600 text-white rounded-lg shadow-lg p-4"
      >
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-medium">
              Connessione Internet Assente
            </h3>
            <p class="mt-1 text-sm text-red-200">
              Alcune funzionalità potrebbero non essere disponibili. Verifica la tua connessione internet.
            </p>
            <div class="mt-3 flex items-center space-x-3">
              <button 
                @click="retry"
                class="text-sm bg-red-700 hover:bg-red-800 px-3 py-1 rounded transition-colors"
              >
                Riprova
              </button>
              <button 
                @click="dismiss"
                class="text-sm text-red-200 hover:text-white transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>

        <!-- Offline indicator -->
        <div class="absolute top-2 right-2">
          <div class="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
        </div>
      </div>
    </Transition>

    <!-- Minimal offline indicator when dismissed -->
    <Transition name="fade">
      <div 
        v-if="!show && isOffline"
        @click="show = true"
        class="fixed bottom-4 right-4 bg-red-600 text-white rounded-full p-3 shadow-lg cursor-pointer hover:bg-red-700 transition-colors"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useOnline } from '@vueuse/core'
import { useAppStore } from '@/stores/app'

const appStore = useAppStore()
const online = useOnline()

const show = ref(true)
const isOffline = computed(() => !online.value)

// Watch for online/offline changes
watch(online, (newValue) => {
  if (!newValue) {
    // Just went offline
    show.value = true
    appStore.showError('Connessione persa')
  } else {
    // Back online
    show.value = false
    appStore.showSuccess('Connessione ripristinata')
  }
})

const retry = async () => {
  // Try to ping the server
  try {
    const response = await fetch('/api/ping', { 
      method: 'HEAD',
      cache: 'no-cache' 
    })
    if (response.ok) {
      show.value = false
      appStore.showSuccess('Connessione ripristinata')
    }
  } catch (error) {
    appStore.showError('Impossibile connettersi al server')
  }
}

const dismiss = () => {
  show.value = false
}

// Auto-retry every 30 seconds when offline
let retryInterval = null

onMounted(() => {
  if (isOffline.value) {
    retryInterval = setInterval(() => {
      if (isOffline.value) {
        retry()
      }
    }, 30000)
  }
})

onUnmounted(() => {
  if (retryInterval) {
    clearInterval(retryInterval)
  }
})
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
